'use client'

import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ElementType,
  type Ref,
  useEffect,
  useRef,
  useState,
} from 'react'

import { REVEAL_STAGGER_MS } from '@/lib/motion'

type RevealProps = ComponentPropsWithoutRef<'div'> & {
  /** Element to render. Defaults to a div; pass `li`, `header`, `section`… */
  as?: ElementType
  /** Position among siblings. Each step delays the entrance by one stagger. */
  index?: number
  /** Extra delay in ms, on top of the index stagger. */
  delay?: number
}

/**
 * Rise-and-fade as the element scrolls into view. The site's only entrance.
 *
 * Each element observes ITSELF rather than inheriting a group's trigger. The
 * previous version put one observer on a wrapper and let every descendant
 * transition off that: on a tall block — four process cards in a row on
 * desktop, stacked on a phone — the group crossed its threshold while the last
 * card was still a screen away, so it had finished animating before you ever
 * saw it. Self-observation costs one observer per element and is always right.
 *
 * The visual definition lives in globals.css under `[data-reveal]`, so the
 * initial (hidden) state is applied by the stylesheet rather than by React —
 * no frame of visible content before the effect runs.
 */
export function Reveal({
  as: Tag = 'div',
  index = 0,
  delay = 0,
  style,
  ref,
  ...rest
}: RevealProps & { ref?: Ref<HTMLElement> }) {
  const localRef = useRef<HTMLElement | null>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const el = localRef.current
    if (!el) return

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShown(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setShown(true)
        observer.disconnect()
      },
      // Fire a little before the element's top edge clears the fold, so it is
      // already settled by the time it is properly in the reading zone.
      { rootMargin: '0px 0px -10% 0px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const revealDelay = index * REVEAL_STAGGER_MS + delay

  return (
    <Tag
      ref={(node: HTMLElement | null) => {
        localRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      }}
      data-reveal={shown ? 'in' : 'out'}
      style={
        revealDelay > 0
          ? ({ ...style, '--reveal-delay': `${revealDelay}ms` } as CSSProperties)
          : style
      }
      {...rest}
    />
  )
}
